import {
  Form,
  FormElements,
} from '@zolteam/zol-react-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import capitalize from 'lodash-es/capitalize';

// Utils & misc
import {
  useFolderNavigation,
} from '@zolteam/zol-react-medialibrary';

import {
  Box,
  Button,
  FolderNavigation,
  ImageContainer,
  MediaInfo,
  SectionInfo,
  Skeleton,
  PAGINATION_DEFAULT_NUMBER,
  PAGINATION_DEFAULT_SIZE,
} from '@zolteam/zol-admin-ui';
import { convertFileSize } from './utils/convertFileSize';

// Components
import FormSkeleton from '../contents/ContentTypeForm/FormSkeleton';

// Services
import { updateMedia } from '../../../services/cortizol/media/mediaService';
import { useMedia } from '../../../services/cortizol/media/mediaQueries';
import { useMediaTree } from '../../../services/cortizol/media-tree/mediaTreeQueries';

// Locales
import fr from '../../../locales/fr.json';
// Store
import { useAppContext } from '../../../store/AppContext';
import useLocale from '../../../domain/cortizol/locale/useLocale';

const FORM_NAME = 'mediaForm';

const SCHEMA = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  type: 'object',
  title: 'New Media',
  properties: {
    name: {
      type: 'string',
      description: 'Nom',
      x_layout: '1/1/1/13',
    },
    descriptionFr: {
      type: 'string',
      description: 'Description (fr)',
      x_layout: '3/1/3/13',
    },
    credit: {
      type: 'string',
      description: 'Crédit',
      x_layout: '5/1/5/13',
    },
  },
  required: [
    'name',
  ],
  additionalProperties: false,
};

/**
 *
 */
function MediaForm() {
  const navigate = useNavigate();
  const [, dispatch] = useAppContext();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { id } = useParams();

  const [searchParam] = useSearchParams();
  const pageNumber = searchParam.get('pageNumber') || PAGINATION_DEFAULT_NUMBER;
  const pageSize = searchParam.get('pageSize') || PAGINATION_DEFAULT_SIZE;
  const folderId = searchParam.get('folderId') || 'root';

  const { localesOptions } = useLocale();

  const {
    data: dataMedia,
    refetch: refetchMedia,
    isLoading: isLoadingMedia,
  } = useMedia(id, {
    select: (data) => {
      const dataDescription = data?.description?.length ? data?.description.reduce(
        (acc, curr) => {
          const capitalizedLocaleId = curr.localeId.charAt(0).toUpperCase()
          + curr.localeId.slice(1);
          acc[`description${capitalizedLocaleId}`] = curr.content;
          return acc;
        },
        {},
      ) : null;

      return {
        ...data,
        ...dataDescription,
      };
    },
  });

  // Get media tree
  const { data: dataRootMediasFolderTree } = useMediaTree(
    { path: 'root' },
    {
      keepPreviousData: true,
      select: (data) => {
        const flattenFolders = (folders) => {
          const result = {};
          folders.forEach((folder) => {
            result[folder.id] = folder;
            if (folder.mediaFolders) {
              const subFolders = flattenFolders(folder.mediaFolders);
              Object.assign(result, subFolders);
            }
          });
          return result;
        };

        const mediaFoldersObjects = flattenFolders(
          data?.mediaFolders || [],
        );
        return {
          root: data,
          version: data?.version,
          ...mediaFoldersObjects,
        };
      },
    },
  );
  const { folderNavigation } = useFolderNavigation(
    dataRootMediasFolderTree,
    folderId,
    t('cortizol.mediaGallery.navigation'),
  );

  const { mutate: mutateUploadCms, isLoading: isLoadingUploadCms } = useMutation({
    mutationFn: (data) => updateMedia(id, data, data.version),
    onSuccess: () => {
      dispatch({
        type: 'SET_MESSAGE',
        payload: { text: t('api.success.updatedContent'), type: 'success' },
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries(['media', id]);
    },
  });

  const onSubmit = async (data) => {
    const dataTransform = {
      name: data.name,
      description: localesOptions.map((locale) => {
        const capitalizedLocaleId = capitalize(locale.value);
        return ({
          localeId: locale.value,
          content: data[`description${capitalizedLocaleId}`],
        });
      }),
      credit: data.credit,
    };
    const media = await refetchMedia();
    mutateUploadCms({
      ...dataTransform,
      version: media?.data?.version,
    });
  };

  // Callbacks
  const handleBackNavigate = () => navigate(`/media?folderId=${folderId}&pageNumber=${pageNumber}&pageSize=${pageSize}`);

  /**
   * Handles folder navigation.
   * @param {object} folder - The folder object.
   */
  const handleFolderNavigation = (folder) => navigate(`/media?folderId=${folder.id}`);

  return (
    <div className="padding-l">
      <Button
        color="transparent"
        icon="arrow-square-left"
        onClick={handleBackNavigate}
      >
        {t('cortizol.mediaGallery.back')}
      </Button>
      <div className="margin-bottom-m padding-top-xs">
        <h1 className="h1-default padding-bottom-xs" style={{ fontWeight: 'bold' }}>
          {t('cortizol.mediaGallery.title')}
        </h1>
        <FolderNavigation
          folders={folderNavigation}
          handleNavigation={handleFolderNavigation}
          filename={dataMedia?.name}
        />
      </div>
      {isLoadingMedia
        ? <FormSkeleton />
        : (
          <Box wrap="nowrap" gap="m">
            <Box>
              {dataMedia?.url ? (
                <ImageContainer src={dataMedia?.url} alt={dataMedia?.alternateText} />
              ) : <Skeleton shape="rectangle" height="400px" width="400px" />}
            </Box>
            <Box direction="column" gap="m" align="normal">
              <MediaInfo>
                <SectionInfo label={t('cortizol.mediaGallery.info.size')} info={convertFileSize(dataMedia?.size)} />
                <SectionInfo
                  label={t('cortizol.mediaGallery.info.dimension')}
                  info={dataMedia?.width && dataMedia?.height
                    ? `${dataMedia?.width}x${dataMedia?.height}` : null}
                />
                <SectionInfo label={t('cortizol.mediaGallery.info.extension')} info={dataMedia?.type} />
                <SectionInfo label={t('cortizol.mediaGallery.info.date')} info={dataMedia?.date} />
                <SectionInfo label={t('cortizol.mediaGallery.info.id')} info={dataMedia?.id} />
              </MediaInfo>

              <Form
                name={FORM_NAME}
                className=""
                callbacks={{
                  onSubmit,
                }}
                defaultValues={dataMedia || undefined}
                schema={SCHEMA}
                translations={{
                  formValidation: fr.cortizol.formValidation,
                }}
              >
                {(formConfig, formInfo) => (
                  <FormElements
                    formConfig={formConfig}
                    formTitle={formInfo.formName}
                  />
                )}
              </Form>

              <Box fullWidth justify="flex-end">
                <Button
                  form={FORM_NAME}
                  isSubmit
                  isLoading={isLoadingUploadCms}
                >
                  {t('cortizol.mediaGallery.edit')}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
    </div>
  );
}

export default MediaForm;
